import {
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@material-ui/core'
import Checkbox from '@material-ui/core/Checkbox'
import AddCommentOutlined from '@material-ui/icons/AddCommentOutlined'
import AssignmentIcon from '@material-ui/icons/Assignment'
import Comment from '@material-ui/icons/Comment'
import CreateIcon from '@material-ui/icons/Create'
import DeleteIcon from '@material-ui/icons/Delete'
import FindInPage from '@material-ui/icons/FindInPage'
import HighlightOffOutlined from '@material-ui/icons/HighlightOffOutlined'
import { makeStyles } from '@material-ui/styles'
import { useEffect, useRef, useState } from 'react'
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd'
import { auditTooltip } from './audit'
import { ItemFilter, MoveItem } from './collection'
import { RadioGroup } from './controls'
import { Audit, CoderNote } from './domain'
import { formApi, Option } from './form'
import { withTooltip } from './format'
import { ListApi } from './list'
import { darkWhite, mediumGray, orange } from './theme'
import { isAuditReviewWorkflow, isReviewWorkflow } from './workflow-api'
import React from 'react'

export interface ColumnEvent<T = any> {
  row: number
  column: Column<T>
  value: T
}

export type ColumnValue<T = any> = (e: ColumnEvent<T>) => string
export type ColumnComponent<T> = (e: ColumnEvent<T>) => JSX.Element | string
export type ColumnListener<T = any> = (e: ColumnEvent<T>) => void
export type ColumnComparator<T = any> = (a: T, b: T) => number
export type ColumnAlign = 'inherit' | 'left' | 'center' | 'right' | 'justify'
export type ColumnDirection = 'asc' | 'desc'

export interface Column<T = any> extends Omit<Option, 'label' | 'options' | 'input'> {
  name?: string
  label?: string
  align?: ColumnAlign
  sortable?: boolean
  sort?: ColumnComparator
  selected?: boolean
  value?: ColumnValue<T>
  click?: ColumnListener<T>
  component?: ColumnComponent<T>
}

function columnValue<T = any>(c: Column, e: ColumnEvent<T> & { dialogMode?: boolean } = {} as any): any {
  if (c.component) {
    return c.component(e)
  }
  return e.value[e.column.name]
}

const iconWidth = 32
export const iconStyle = {
  width: iconWidth,
  maxWidth: iconWidth,
  minWidth: iconWidth,
}

export function deleteColumn<T = any>(
  click: ColumnListener<T>,
  activeCheck?: (a: T) => boolean,
  label = '',
  id?: (e: ColumnEvent<T>) => string,
): Column<T> {
  const component = activeCheck
    ? (e: ColumnEvent<T>) =>
        activeCheck(e.value) ? (
          <IconButton id={id && id(e)} color="primary" onClick={() => click(e)}>
            <DeleteIcon />
          </IconButton>
        ) : (
          <IconButton id={id && id(e)} color="primary" style={{ pointerEvents: 'none' }}>
            <DeleteIcon htmlColor={mediumGray} />
          </IconButton>
        )
    : (e: ColumnEvent<T>) => (
        <IconButton id={id && id(e)} color="primary" onClick={() => click(e)}>
          <DeleteIcon />
        </IconButton>
      )

  return {
    component,
    name: 'delete',
    label,
    click,
    align: 'center',
    style: iconStyle,
  }
}

export function shimColumn<T = any>(): Column<T> {
  return {
    name: 'shim',
    align: 'center',
    style: iconStyle,
  }
}

export function auditColumn<T = any>(click: ColumnListener<T>, label = ''): Column<T> {
  return {
    name: 'note',
    label,
    click,
    align: 'center',
    style: iconStyle,
    component: (e: ColumnEvent) => {
      const audit: Audit = (e.value as any).audit
      const i = audit?.changeReasons?.length
      const validAudit = audit?.changeReasonCode || !isNaN(i)
      let tooltip = auditTooltip(audit)
      return validAudit
        ? withTooltip(
            tooltip,
            <IconButton color="primary" onClick={() => click(e)}>
              <FindInPage htmlColor={orange} />{' '}
            </IconButton>,
            'bottom',
          )
        : withTooltip(
            'Audit Info',
            <IconButton color="primary" disabled={true}>
              <FindInPage htmlColor={mediumGray} />{' '}
            </IconButton>,
            'bottom',
          )
    },
  }
}

export function editColumn<T = any>(
  click: ColumnListener<T>,
  activeCheck?: (a: T) => boolean,
  label = '',
  id?: (e: ColumnEvent<T>) => string,
): Column<T> {
  const component = activeCheck
    ? (e: ColumnEvent<T>) =>
        activeCheck(e.value) ? (
          <IconButton id={id && id(e)} color="primary" onClick={() => click(e)}>
            <CreateIcon />
          </IconButton>
        ) : (
          <IconButton id={id && id(e)} color="primary" style={{ pointerEvents: 'none' }}>
            <CreateIcon htmlColor={mediumGray} />
          </IconButton>
        )
    : (e: ColumnEvent) => (
        <IconButton id={id && id(e)} color="primary" onClick={() => click(e)}>
          <CreateIcon />
        </IconButton>
      )

  return {
    component,
    name: 'edit',
    label,
    click,
    align: 'center',
    style: iconStyle,
  }
}

export function noteColumn<T = any>(click: ColumnListener<T>, label = ''): Column<T> {
  return {
    name: 'note',
    label,
    click,
    align: 'center',
    style: iconStyle,
    component: (e: ColumnEvent<T>) => {
      const note: CoderNote = (e.value as any).note
      return note ? (
        withTooltip(
          note.note,
          <IconButton color="primary" style={{ pointerEvents: 'all' }} onClick={() => click(e)}>
            <Comment />{' '}
          </IconButton>,
          'bottom',
        )
      ) : (
        <IconButton color="primary" onClick={() => click(e)}>
          <AddCommentOutlined htmlColor={mediumGray} />
        </IconButton>
      )
    },
  }
}

function ActiveIconOr(item: { active: boolean }, line: () => number) {
  if (item?.active) {
    return (
      <Typography style={{ paddingLeft: 5 }} variant="subtitle2">
        {line() || ''}
      </Typography>
    )
  }
  return <HighlightOffOutlined htmlColor={mediumGray} fontSize="small" />
}

export function rowColumn(props?: Column): Column {
  return {
    ...{
      name: 'line',
      component: (e) => ActiveIconOr(e.value, () => e.row + 1),
      style: {
        width: 24,
        maxWidth: 24,
        minWidth: 24,
        height: 45,
        align: 'center',
        textAlign: 'center',
      },
    },
    ...props,
  }
}

export function lineNumberColumn(props?: Column<{ active: boolean; lineNumber: number }>): Column {
  return {
    ...{
      name: 'line',
      component: (e) => ActiveIconOr(e.value, () => e.value?.lineNumber),
      style: {
        width: 24,
        maxWidth: 24,
        minWidth: 24,
        paddingBottom: 1,
        align: 'center',
        textAlign: 'center',
      },
    },
    ...props,
  }
}

export function checkboxColumn<T = any>(click: ColumnListener<T>, label = ''): Column<T> {
  return {
    name: 'checkbox',
    label,
    click,
    align: 'center',
    style: { width: 14, maxWidth: 14 },
    component: (e) => (
      <Checkbox
        checked={e.column.selected}
        value={e.column}
        onClick={() => {
          e.column.selected = !e.column.selected
          click(e)
        }}
      />
    ),
  }
}

export function assignmentColumn<T = any>(click: ColumnListener<T>, label = ''): Column<T> {
  return {
    name: 'assignment',
    label,
    click,
    align: 'center',
    component: (e: ColumnEvent<T>) => (
      <IconButton color="primary" onClick={() => click(e)}>
        <AssignmentIcon />
      </IconButton>
    ),
  }
}

const defaultTableStyles = makeStyles({
  table: {
    minWidth: 20,
    width: '100%',
  },
  container: {
    overflow: 'hidden',
  },
})

function columnComparator<T = any>(column: Column<T>): ColumnComparator<T> {
  const comparator = column.sort || ((a: string, b: string) => a.toString().localeCompare(b))
  return (a, b) => {
    return comparator(a[column.name] || '', b[column.name] || '')
  }
}

export interface ColumnTableProps<T = any> {
  api: ListApi<T>
  columns: Column<T>[]
  containerProps?: object
  draggable?: boolean
  index?: boolean
  onMove?: (item: MoveItem) => void
  headless?: boolean
  readonly?: boolean
  name?: string
  rowColor?: (item: T) => string | undefined
  label?: string
}

export function ColumnTable<T>(props: ColumnTableProps) {
  const { columns, containerProps, draggable, api, onMove, headless, readonly, name, rowColor, label } = props
  const { data, actions } = api
  const items = actions
  const values = data((s) => s.values)
  const classes = defaultTableStyles()

  const [direction, setDirection] = useState<ColumnDirection>('desc')
  const [sorted, setSorted] = useState<T[]>(values)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogRow, setDialogRow] = useState<T | null>(null)

  function sortRows(c: Column) {
    const comparator = columnComparator(c)
    const dir = direction === 'asc' ? 1 : -1
    values.sort((a, b) => comparator(a, b) * dir)
    setDirection(direction === 'asc' ? 'desc' : 'asc')
    setSorted(values.slice())
  }
  if (draggable) {
    values?.forEach((r: any, i) => (r.id = i.toString()))
  }
  function onDragEnd(result: any) {
    const { source, destination } = result
    // dropped outside the list
    // console.log('onDragEnd', source, destination)
    if (!destination) {
      return
    }
    if (source.index !== destination.index) {
      const item = { currentIndex: source.index, newIndex: destination.index }
      if (onMove) {
        onMove(item)
      } else {
        items.move(item)
      }
    }
  }

  const dragTargetProps = draggable ? { component: DroppableComponent(onDragEnd) } : null
  const headerStyle = { backgroundColor: 'white' }

  useEffect(() => {
    setSorted(values)
  })

  return (
    <TableContainer
      style={{ pointerEvents: readonly ? 'none' : undefined }}
      component={(props) => <Paper {...{ ...props, ...containerProps }} />}
      className={classes.container}
      data-field="table"
    >
      <Table className={classes.table} stickyHeader size="small" data-field={props?.name}>
        {!headless && (
          <TableHead>
            <TableRow>
              {columns.map((c) => (
                <TableCell
                  key={c?.label || c?.name}
                  style={{ ...c.style, ...headerStyle }}
                  align={c.align ? c.align : 'left'}
                >
                  {c.sortable || c.sort ? (
                    <TableSortLabel key={c.name} direction={direction} onClick={() => sortRows(c)}>
                      {c.label}
                    </TableSortLabel>
                  ) : (
                    c.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
        )}
        <TableBody {...dragTargetProps}>
          {sorted.map((value, row) => {
            const dragStartProps = draggable ? { component: DraggableComponent((value as any).id, row) } : null
            const active = (value as any).active

            const color = active ? undefined : mediumGray
            let backgroundColor = active ? undefined : darkWhite

            if (rowColor) {
              backgroundColor ||= rowColor(value)
            }

            const textDecoration = active ? undefined : 'line-through'

            return (
              <TableRow key={row.toString()} {...dragStartProps}>
                {columns.map((c, index) => (
                  <TableCell
                    id={'cell_' + name + '_' + (c?.label != '' ? c?.label : c.name) + '_' + row + '_' + index}
                    key={c.name}
                    style={{
                      ...{
                        color,
                        backgroundColor,
                        textDecoration,
                        paddingTop: 0,
                        paddingBottom: 0,
                      },
                      ...c.style,
                      ...(readonly && c.name !== 'edit' && c.name !== 'delete' && c.name !== 'shim' && c.name !== "note" && c.name !== "line" && c.label !== "" ? { pointerEvents: 'auto' } : {}),
                    }}
                    align={c.align ? c.align : 'left'}
                  >
                    {columnValue(c, { row, value, column: c })}
                  </TableCell>
                ))}
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

const DroppableComponent = (onDragEnd: (result, provided) => void) => (props: any) => {
  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId={'1'} direction="vertical">
        {(provided) => {
          return (
            <TableBody ref={provided.innerRef} {...provided.droppableProps} {...props}>
              {props.children}
              {provided.placeholder}
            </TableBody>
          )
        }}
      </Droppable>
    </DragDropContext>
  )
}
const getDraggedItemStyle = (isDragging: boolean, draggableStyle: any) => ({
  ...draggableStyle,
  ...(isDragging && {
    background: 'rgb(235,235,235)',
    display: 'table',
  }),
})

const DraggableComponent = (id: any, index: number) => (props: any) => {
  return (
    <Draggable draggableId={id} index={index}>
      {(provided: any, snapshot: any) => {
        const { style } = provided?.draggableProps?.style

        return (
          <TableRow
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            style={{
              ...getDraggedItemStyle(snapshot.isDragging, provided.draggableProps.style),
              ...style,
            }}
            {...props}
          >
            {props.children}
          </TableRow>
        )
      }}
    </Draggable>
  )
}

export interface CollectionTableProps<T = any> {
  columns: Column<T>[]
  api: ListApi<T>
  containerProps?: object
  noEdit?: boolean
  draggable?: boolean
  index?: boolean
  onMove?: (item: MoveItem) => void
  headless?: boolean
  readonly?: boolean
  name?: string
  rowColor?: (item: T) => string | undefined
  label?: string
}
export function CollectionTable<T = any>(props: CollectionTableProps<T>) {
  const { columns, api, containerProps, draggable, onMove, headless, readonly, name, rowColor, label } = props

  return (
    <ColumnTable
      api={api}
      onMove={onMove}
      containerProps={containerProps}
      columns={columns || []}
      headless={headless}
      draggable={draggable}
      readonly={readonly}
      name={name}
      rowColor={rowColor}
      label={label}
    />
  )
}

type FilterControlProps = {
  api: ListApi
  debug?: boolean
}

export function FilterControl(props: FilterControlProps) {
  const isReview = isAuditReviewWorkflow()

  const { api, debug } = { ...props }
  const form = useRef(
    formApi<{ filter: ItemFilter }>({
      values: { filter: api.data.getState().filter || 'all' },
      fields: {
        filter: {
          row: true,
          small: true,
          style: { flexWrap: 'nowrap' },
          options: [
            { label: 'Active', value: 'active' },
            { label: 'Deleted', value: 'inactive' },
            { label: 'All', value: 'all' },
          ],
          onChange: (v) => {
            api.actions.filter(v)
          },
        },
      },
    }),
  ).current

  return debug && isReview ? (
    <div onClick={(e) => e.stopPropagation()}>
      <RadioGroup {...form.controls.filter} />
    </div>
  ) : null
}
